import html2pdf from "html2pdf.js";
import breezeLogoUrl from "../../../../../icons/icon_files/breeze_full_logo.svg";
import {
  COLLAPSE_ICON_CLASSNAME,
  COLLAPSE_SECTION_CLASSNAME,
  CONTENT_SECTION_CLASSNAME,
} from "./PotentialCollapsibleSection";
import {
  HEADER_BUTTONS_CLASSNAME,
  POTENTIAL_DESCRIPTION_CLASSNAME,
  POTENTIAL_HEADER_CLASSNAME,
} from "./PotentialModal";
import { SECURITY_DATA_GRID_CLASSNAME } from "./SecuritySection";

function addBreezeLogoOnTop(fullElement) {
  const img = document.createElement("img");
  img.crossOrigin = "anonymous";
  img.src = breezeLogoUrl;
  img.alt = "Icon";
  img.style.width = "230px";
  img.style.height = "40px";

  const logoContainer = document.createElement("div");
  logoContainer.style.display = "flex";
  logoContainer.style.justifyContent = "center";
  logoContainer.appendChild(img);

  fullElement.appendChild(logoContainer);
}

function createNewPageElementForPDF() {
  const pageElement = document.createElement("div");
  pageElement.style.cssText = `
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    background-color: #ffffff;
    padding: 32px 16px;
  `;
  addBreezeLogoOnTop(pageElement);
  return pageElement;
}

function getHeaderCloneElement(containerRef) {
  const headerClone = containerRef
    .querySelector(`.${POTENTIAL_HEADER_CLASSNAME}`)
    .cloneNode(true);
  const buttonsSection = headerClone.querySelector(
    `.${HEADER_BUTTONS_CLASSNAME}`
  );
  buttonsSection.remove();
  return headerClone;
}

function prettifyContentElement(contentElement) {
  // replace the boxShadow that looks weird in the pdf with a normal border
  contentElement.style.boxShadow = "none";
  contentElement.style.backgroundColor = "white";
  contentElement.style.border = "1px solid rgb(224, 224, 224)";
  // remove the collapse icon button
  contentElement.querySelector(`.${COLLAPSE_ICON_CLASSNAME}`).remove();
}

function createNISTChip(nistFunction, colors) {
  if (!nistFunction) nistFunction = "Protect";
  const textColor = colors.text_primary;
  const backgroundColor = colors[`nist_${nistFunction.toLowerCase()}_main`];
  const strokeColor = colors[`nist_${nistFunction.toLowerCase()}_stroke`];

  const chip = document.createElement("span");
  chip.style.cssText = `
    width: 100px;
    height: 24px;
    display: flex; 
    justify-content: center; 
    align-items: center; 
    background-color: ${backgroundColor};
    color: ${textColor};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid ${strokeColor};
  `;
  chip.textContent = nistFunction;
  return chip;
}

function createFullControlsListElement(integrationInfo, colors) {
  const controlsContainer = document.createElement("div");
  controlsContainer.style.cssText = `
    display: flex; 
    flex-direction: column; 
    gap: 16px; 
    margin-bottom: 8px; 
    padding-left: 8px;
  `;

  const listHeader = document.createElement("div");
  listHeader.style.cssText = `
    display: flex;
    justify-content: space-between;
    font-weight: 500;
    font-size: 14px;
    color: #333333;
    padding-right: 24px;
  `;
  listHeader.innerHTML = `<span>Name</span><span>NIST CSF</span>`;
  controlsContainer.appendChild(listHeader);

  const divider = document.createElement("div");
  divider.style.cssText = `
    height: 1px;
    background-color: #e0e0e0;
    margin: 0;
  `;
  controlsContainer.appendChild(divider);

  const sortedControls = [...(integrationInfo.controls || [])].sort((a, b) =>
    a.nist_function.localeCompare(b.nist_function)
  );

  sortedControls.forEach((control) => {
    const controlElement = document.createElement("div");
    controlElement.style.cssText = `
      display: flex; 
      justify-content: space-between; 
      font-size: 14px;
      color: #333333;
      align-items: center;
    `;
    const controlName =
      control.name.length > 80 ? control.name.slice(0, 80) + "…" : control.name;
    controlElement.innerHTML = `<span>${controlName}</span>`;
    const NISTChip = createNISTChip(control.nist_function, colors);

    controlElement.appendChild(NISTChip);
    controlsContainer.appendChild(controlElement);
  });

  return controlsContainer;
}

function expandAllCollapsibleSections(containerClone) {
  const collapsibleSections = containerClone.querySelectorAll(
    `.${COLLAPSE_SECTION_CLASSNAME}`
  );
  collapsibleSections.forEach((section) => {
    const collapsibleBox = section.children[0];
    collapsibleBox.style.height = "100%";
    collapsibleBox.style.maxHeight = "100%";
    collapsibleBox.style.visibility = "visible";
  });
}

function createAllContentElements(containerRef, integrationInfo, colors) {
  const containerClone = containerRef.cloneNode(true);
  expandAllCollapsibleSections(containerClone);

  const contentSections = containerClone.querySelectorAll(
    `.${CONTENT_SECTION_CLASSNAME}`
  );

  const modifiedElements = [];
  contentSections.forEach((section) => {
    prettifyContentElement(section);
    const dataGridElementContainer = section.querySelector(
      `.${SECURITY_DATA_GRID_CLASSNAME}`
    );
    if (dataGridElementContainer) {
      dataGridElementContainer.children[0].remove();
      dataGridElementContainer.appendChild(
        createFullControlsListElement(integrationInfo, colors)
      );
    }
    modifiedElements.push(section);
  });

  return modifiedElements;
}

export const handlePDFExport = async (
  containerRef,
  integrationName,
  integrationInfo,
  colors
) => {
  if (!containerRef) return;

  const entirePageElement = createNewPageElementForPDF();

  const headerClone = getHeaderCloneElement(containerRef);
  const descriptionClone = containerRef
    .querySelector(`.${POTENTIAL_DESCRIPTION_CLASSNAME}`)
    .cloneNode(true);

  const allContentCloneElements = createAllContentElements(
    containerRef,
    integrationInfo,
    colors
  );

  entirePageElement.appendChild(headerClone);
  entirePageElement.appendChild(descriptionClone);
  allContentCloneElements.forEach((element) => {
    entirePageElement.appendChild(element);
  });

  const contentWidthMm = 210; // A4 width
  let contentHeightMm = 297; // A4 height

  if (integrationInfo.controls?.length > 8) {
    contentHeightMm += (integrationInfo.controls.length - 8) * 12;
  }

  await html2pdf()
    .set({
      filename: `Breeze_integration_${integrationName}.pdf`,
      html2canvas: { useCORS: true },
      jsPDF: {
        unit: "mm",
        format: [contentWidthMm, contentHeightMm],
        orientation: "portrait",
      },
    })
    .from(entirePageElement)
    .save();
};
